import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef } from "react";
import { getQueryParams, stringifyQueryString } from "../helper/utils";

import { CityContext } from "../contexts/city-context-provider";
import { ViewState } from "react-map-gl/mapbox";
import { WebMercatorViewport } from "@math.gl/web-mercator";
import { useLocation } from "react-router-dom";

export type ItemParam = { viewport?: ViewState } & {
  [key: string]: string | string[] | ViewState | undefined;
};

export const useSetQueryParams = () => {
  const currentParam = getQueryParams();
  const currentViewport = useRef<ViewState>();
  const { setViewport } = useContext(CityContext);

  const location = useLocation();

  useEffect(() => {
    return () => {
      if (currentViewport.current) {
        setViewport(currentViewport.current);
      }
    };
  }, [setViewport]);

  const setQueryParams = useCallback(
    (params: ItemParam, options?: { merge?: boolean }) => {
      const { viewport, ...rest } = params;
      let view = currentParam.view;
      if (viewport) {
        view = `${viewport.latitude},${viewport.longitude},${viewport.zoom}`;
      }

      const newParams = {
        view,
        ...rest,
      };

      const mergeParams = options?.merge
        ? {
            ...currentParam,
            view,
            ...rest,
          }
        : newParams;
      currentViewport.current = viewport;
      window.history.replaceState(null, "", `${location.pathname}?${stringifyQueryString(mergeParams)}`);
    },
    [currentParam, location.pathname],
  );

  return setQueryParams;
};

export type QueryParamByViewPort = {
  viewState: ViewState;
  viewport: Pick<ViewState, "zoom">;
  setBounding?: Dispatch<SetStateAction<number[][] | undefined>>;
  cityID: string;
  policies?: string[];
};

export const useQueryParamByViewPort = () => {
  const setQueryParams = useSetQueryParams();
  return function setQueryParamByViewport({
    viewState,
    viewport,
    setBounding,
    cityID,
    policies,
  }: QueryParamByViewPort) {
    const elmViewPort = new WebMercatorViewport({ ...viewState, height: window.innerHeight, width: window.innerWidth });
    const currentZoom = Math.round(viewport.zoom);
    const nearBy = elmViewPort.getBoundingRegion({ z: currentZoom });
    nearBy.forEach((item) => {
      item.splice(2, 1);
    });
    if (nearBy) {
      if (setBounding) {
        setBounding(nearBy);
      }
      setQueryParams(
        {
          cityID,
          viewport: viewState,
          policies,
        },
        { merge: true },
      );
    }
  };
};
